
import { throttle, uniqueId } from 'lodash'

export default {
  name: 'Map',

  props: {
    initialStyle: {
      type: String,
      default: 'satellite',
      validator: prop => ['streets', 'satellite', 'light'].includes(prop)
    },

    centerLngLat: {
      type: Array,
      default: null
    },

    isLoading: {
      type: Boolean,
      default: false
    },

    mapPaddingTop: {
      type: Number,
      default: 40
    },

    mapPaddingBottom: {
      type: Number,
      default: 40
    },

    zoomLevel: {
      type: Number,
      default: null
    },

    hasScrollZoom: {
      type: Boolean,
      default: true
    },

    hasZoomControls: {
      type: Boolean,
      default: true
    },

    hasFullScreenControl: {
      type: Boolean,
      default: true
    },

    hasStyleControls: {
      type: Boolean,
      default: false
    },

    hasCompactStyleControls: {
      type: Boolean,
      default: false
    },

    // Rather than passing many props, just override entire mapbox init config
    options: {
      type: Object,
      default: () => ({})
    },

    mobileBleedTop: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      id: null,
      map: null,
      supportsWebGL: false,
      activeStyle: 'streets',
      styleConfig: {
        streets: 'mapbox://styles/mapbox/streets-v11',
        satellite: 'mapbox://styles/mapbox/satellite-streets-v11',
        light: 'mapbox://styles/mapbox/light-v10'
      },

      thirdPartyScriptsToLoad: [
        { id: 'mapbox-gl', url: 'https://api.mapbox.com/mapbox-gl-js/v2.9.2/mapbox-gl.js' },
        {
          id: 'mapbox-gl-draw',
          url: 'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-draw/v1.3.0/mapbox-gl-draw.js'
        }
      ],

      thirdPartyCssToLoad: [
        { id: 'mapbox-gl-css', url: 'https://api.mapbox.com/mapbox-gl-js/v2.9.2/mapbox-gl.css' },
        {
          id: 'mapbox-gl-draw-css',
          url: 'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-draw/v1.3.0/mapbox-gl-draw.css'
        }
      ]
    }
  },

  watch: {
    bounds(bounds) {
      if (bounds.length > 0 && this.map) {
        this.fitBounds(bounds)
      }
    }
  },

  mounted() {
    this.supportsWebGL = this.isWebGLSupported()

    if (!this.supportsWebGL) {
      console.warn('WebGL not supported')
    }
  },

  created() {
    // Unique ID has to be generated on mounted otherwise it will be different when hydrated vs SSR
    this.id = uniqueId('map-')
  },

  methods: {
    isWebGLSupported() {
      try {
        const canvas = document.createElement('canvas')

        const isSupported =
          !!window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))

        if (!isSupported) {
          this.$analytics.addEvent('WebGL not supported')
          return false
        }

        return true
      } catch (e) {
        this.$analytics.addEvent('WebGL not supported')
        return false
      }
    },

    async onMapScriptReady() {
      await this.$elementReady.ref(this.id, this)

      if (!window.mapboxgl) {
        console.log('Mapbox script failed to load')
        return
      }

      window.mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN

      // Get center of our current organisation country if one hasn't been defined
      const centerLngLat = this.centerLngLat ? this.centerLngLat : [-4.174804, 54.559322]

      const zoomLevel = this.zoomLevel ? this.zoomLevel : 4

      this.map = new window.mapboxgl.Map({
        container: this.id,
        style: this.styleConfig[this.initialStyle],
        center: centerLngLat,
        zoom: zoomLevel,
        attributionControl: false,
        scrollZoom: this.hasScrollZoom,
        ...this.options
      })

      this.setupEvents()

      this.activeStyle = this.initialStyle

      this.loadControls()
    },

    setupEvents() {
      // We must wait for the map style to load before adding layers to prevent errors
      // this.map.on('load', () => {
      //   this.$emit('ready', this.map)
      // })

      // Switching between base styles requires us to re-apply layers
      this.map.on('style.load', () => {
        this.$emit('styles-loaded')
        this.$emit('ready', this.map)
      })

      this.map.on('zoom', () => {
        this.setViewAdjusted()
      })

      this.map.on('click', event => {
        this.$emit('click', event)

        // Keeping in debug for now as useful for positioning during development
        // this.$log.debug('Map click', `[${event.lngLat.lng},${event.lngLat.lat}] ${this.map.getZoom()}`)
      })

      this.map.on('dragend', () => {
        this.setViewAdjusted()
      })

      this.map.on('dragstart', () => {
        this.$emit('drag-start')
      })

      // Allow scroll zooming even if disabled if the user is holding a modifier key
      this.map.on('wheel', event => {
        if (this.hasScrollZoom) {
          return
        }

        if (event.originalEvent.ctrlKey || event.originalEvent.metaKey || event.originalEvent.altKey) {
          // Prevent chrome/firefox default behavior
          event.originalEvent.preventDefault()

          // Enable zoom only if it's disabled
          if (!this.map.scrollZoom._enabled) {
            this.map.scrollZoom.enable()
          }
        } else if (this.map.scrollZoom._enabled) {
          // Disable zoom only if it's enabled
          this.map.scrollZoom.disable()
        }
      })
    },

    setViewAdjusted(hasViewBeenAdjusted = true) {
      this.$emit('view-adjusted', hasViewBeenAdjusted)
    },

    loadControls() {
      if (this.hasZoomControls) {
        this.map.addControl(
          new window.mapboxgl.NavigationControl({
            showCompass: false
          })
        )

        if (this.hasFullScreenControl) {
          this.map.addControl(new window.mapboxgl.FullscreenControl())
        }
      }

      this.$emit('controlsReady')
    },

    fitBounds: throttle(
      function (lngLats = null, isAnimated = false) {
        if (!lngLats) {
          return false
        }

        let boundsExtended = false

        const bounds = new window.mapboxgl.LngLatBounds()

        lngLats.forEach(lngLat => {
          boundsExtended = true
          bounds.extend(lngLat)
        })

        const fitBoundOptions = {
          padding: { top: this.mapPaddingTop, bottom: this.mapPaddingBottom, left: 100, right: 100 },
          maxZoom: 13
        }

        if (!isAnimated) {
          fitBoundOptions.duration = 0
        } else {
          fitBoundOptions.linear = false
          fitBoundOptions.duration = 2000
        }

        if (boundsExtended) {
          setTimeout(() => {
            this.map.fitBounds(bounds, fitBoundOptions)

            this.setViewAdjusted(false)
          }, 200)

          return
        }

        this.setViewAdjusted(false)
      },
      1000,
      { leading: true }
    ),

    switchStyle(style) {
      this.map.setStyle(this.styleConfig[style])

      this.activeStyle = style

      this.$analytics.addEvent('Switched map style', { style })
    },

    flyTo(lngLat, zoomLevel = 15) {
      this.map.flyTo({
        center: lngLat,
        zoom: zoomLevel
      })

      this.setViewAdjusted()
    },

    resize() {
      this.$log.debug('Resizing map')

      this.map.resize()
    }
  }
}
